import { DateTime } from "luxon"
import { lessonsHistoryStart, theme } from "../settings/config"
import {
	faBaseball,
	faTableTennisPaddleBall,
	faPersonRunning,
} from "@fortawesome/free-solid-svg-icons"

export function emailIsValid(email) {
	return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
}

// Quella con cui si entra: Firebase ne accetta da 6 caratteri, e gli
// accessi creati prima vanno ancora bene.
export const passwordIsValid = password => {
	return password?.length >= 6
}

// Quella che si sceglie adesso, registrandosi o attivando l'account: con
// 6 caratteri si indovina in fretta.
export const NEW_PASSWORD_MIN_LENGTH = 8

export const newPasswordIsValid = password => {
	return typeof password === "string" && password.length >= NEW_PASSWORD_MIN_LENGTH
}

export const findSelectedPlayerInState = (players, playerToSearch) => {
	return players.find(player => `${player.playerName} ${player.playerSurname}` === playerToSearch)
}

// Nome completo dell'allievo: gli allievi creati al volo durante una lezione
// spesso hanno solo il nome, quindi il cognome può mancare. Se manca tutto e
// resta solo il soprannome, quello è il suo nome: "Bibo" si scrive "Bibo".
export const getPlayerFullName = player => {
	const fullName = [player?.playerName, player?.playerSurname].filter(Boolean).join(" ").trim()
	return fullName || player?.nickname?.trim() || ""
}

// Come si scrive un allievo quando bisogna riconoscerlo tra altri: il
// soprannome fa da discriminante dove il nome completo non basta. Il nome
// salvato resta quello vero — questa è solo un'etichetta da leggere.
export const getPlayerLabel = player => {
	const fullName = getPlayerFullName(player)
	const nickname = getPlayerNickname(player)
	return nickname ? `${fullName} (${nickname})` : fullName
}

// Il soprannome da mostrare accanto al nome: se è l'unica cosa che sappiamo
// dell'allievo lo sta già scrivendo il nome, e ripeterlo sarebbe rumore.
export const getPlayerNickname = player => {
	const nickname = player?.nickname?.trim()
	return nickname && nickname !== getPlayerFullName(player) ? nickname : ""
}

// Confronto tra nomi scritti a mano: ignora maiuscole e spazi doppi
export const normalizeName = name =>
	typeof name === "string" ? name.toLowerCase().replace(/\s+/g, " ").trim() : ""

// L'allievo che il maestro sta scrivendo, tra quelli che ci sono già. Può
// aver scritto il nome completo, l'etichetta intera che gli propone l'elenco
// ("Marco Rossi (Bibo)") o il solo soprannome: valgono tutti, altrimenti
// scrivere "Bibo" creerebbe un doppione di un allievo che c'è già. L'ordine
// conta: il nome vero viene prima del soprannome, così un soprannome che
// somiglia al nome di un altro non se lo porta via.
export const findKnownPlayer = (typedName, players) => {
	const normalizedName = normalizeName(typedName)
	if (!normalizedName) return null

	const candidates = (players ?? []).filter(player => !isArchivedPlayer(player))
	return (
		candidates.find(player => normalizeName(getPlayerFullName(player)) === normalizedName) ??
		candidates.find(player => normalizeName(getPlayerLabel(player)) === normalizedName) ??
		candidates.find(player => normalizeName(player.nickname) === normalizedName) ??
		null
	)
}

// Come si scompone quello che il maestro ha scritto nel campo partecipante.
// Una parola sola non è un nome monco: è il soprannome con cui lo chiama in
// campo ("Bibo"). Da due parole in su sono nome e cognome.
export const splitTypedPlayerName = typedName => {
	const tokens = normalizeName(typedName) ? typedName.trim().split(/\s+/) : []

	if (tokens.length === 0) return { playerName: "", playerSurname: "", nickname: "" }
	if (tokens.length === 1) return { playerName: "", playerSurname: "", nickname: tokens[0] }

	return { playerName: tokens[0], playerSurname: tokens.slice(1).join(" "), nickname: "" }
}

// Un allievo archiviato resta in archivio ma sparisce da liste e ricerche
export const isArchivedPlayer = player => player?.archived === true

// Da quando le lezioni salvano `partecipantIDs` si può cercare per allievo:
// prima di questa data lo storico non c'è
export const getLessonsHistoryStartLabel = () =>
	DateTime.fromISO(lessonsHistoryStart).setLocale("it").toFormat("LLLL yyyy")

// Le lezioni salvano i partecipanti denormalizzati: quelle create al volo
// hanno solo il nome, le altre anche il playerID
export const getLessonPartecipant = (lesson, playerID, playerFullName) => {
	const normalizedFullName = normalizeName(playerFullName)
	return (lesson?.partecipants || []).find(partecipant => {
		if (partecipant?.playerID) return partecipant.playerID === playerID
		if (!normalizedFullName) return false
		return normalizeName(partecipant?.playerName) === normalizedFullName
	})
}

// Le date degli allievi arrivano sia in ISO (yyyy-LL-dd) che nel vecchio
// formato del foglio Google (dd/LL/yyyy): le normalizzo prima di usarle
export const parsePlayerDate = value => {
	if (!value) return null
	if (typeof value?.toDate === "function") return DateTime.fromJSDate(value.toDate())

	const isoDate = DateTime.fromISO(value)
	if (isoDate.isValid) return isoDate

	const italianDate = DateTime.fromFormat(value, "dd/LL/yyyy")
	return italianDate.isValid ? italianDate : null
}

// valore per un <input type="date">
export const toDateInputValue = value => parsePlayerDate(value)?.toFormat("yyyy-LL-dd") ?? ""

export const formatPlayerDate = value =>
	parsePlayerDate(value)?.setLocale("it").toFormat("dd LLL yyyy") ?? ""

// ──────────────────────────────────────────────────────────────────────────
// LA DATA DI NASCITA STA GIÀ NEL CODICE FISCALE
//
// Chiederla a parte vuol dire chiedere due volte la stessa cosa, e dare la
// possibilità di scriverla diversa. Le cifre 7-8 sono l'anno, la lettera 9 il
// mese, le 10-11 il giorno — più 40 per le donne.
//
// I codici con omocodia (dove alcune cifre diventano lettere) non vengono
// riconosciuti: meglio non capirli e far scrivere la data a mano che
// indovinarla storta su una fattura.
// ──────────────────────────────────────────────────────────────────────────
const CF_MONTHS = { A: 1, B: 2, C: 3, D: 4, E: 5, H: 6, L: 7, M: 8, P: 9, R: 10, S: 11, T: 12 }

const CF_PATTERN = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/

export const birthdayFromCF = cf => {
	const code = (cf ?? "").trim().toUpperCase()
	if (!CF_PATTERN.test(code)) return ""

	const month = CF_MONTHS[code[8]]
	const day = Number(code.slice(9, 11)) % 40
	if (!month || day < 1) return ""

	// l'anno ha due cifre: se cadrebbe nel futuro è del secolo scorso
	const shortYear = Number(code.slice(6, 8))
	const year = shortYear + 2000 > DateTime.now().year ? 1900 + shortYear : 2000 + shortYear

	const birthday = DateTime.fromObject({ year, month, day })
	return birthday.isValid ? birthday.toFormat("yyyy-LL-dd") : ""
}

export const getPlayerAge = birthday => {
	const parsedBirthday = parsePlayerDate(birthday)
	return parsedBirthday ? Math.floor(DateTime.now().diff(parsedBirthday, "years").years) : null
}

export const isMinorPlayer = birthday => {
	const age = getPlayerAge(birthday)
	return age !== null && age < 18
}

export const hasValidDocument = (player, document) => {
	const expirationDate = parsePlayerDate(player?.[document])
	const expirationDays = expirationDate
		? Math.round(expirationDate.diff(DateTime.now(), "days").days)
		: 0
	return { expirationDays: expirationDays, isValid: expirationDays > 0 }
}

export const playerCanPlay = (selectedPlayer, players) => {
	if (selectedPlayer.player && Object.keys(players).length > 0) {
		var playerFromState = findSelectedPlayerInState(players, selectedPlayer.player)
		if (playerFromState) {
			return (
				hasValidDocument(playerFromState, "medCertificateExpiration").isValid &&
				(!selectedPlayer.paymentMethod === "Abbonamento" ||
					hasValidDocument(playerFromState, "subscriptionExpiration").isValid) &&
				hasValidDocument(playerFromState, "membershipExpiration").isValid
			)
		}
	}
	return true
}

// ──────────────────────────────────────────────────────────────────────────
// CHI TIENE LE LEZIONI DI CHI
//
// Le lezioni di un collega le tocca chi dirige il circolo — tutte: è il suo
// lavoro — oppure chi il direttore ha messo a gestire quel collega. Quel
// legame vale in un circolo solo, quello in cui il direttore l'ha deciso:
// sul documento del maestro sta in `managedCoachesBySchool`, circolo per
// circolo, perché chi dirige un circolo non deve poter dare a qualcuno le
// lezioni che un collega tiene in un altro.
//
// Le regole di Firestore dicono la stessa cosa, con le claim
// `managedSchoolIDs` e `managedCoaches` del token: chi cambia qui cambia
// anche `firestore.rules`.
// ──────────────────────────────────────────────────────────────────────────
export const managesSchool = (coach, schoolID) =>
	Boolean(schoolID && coach?.managedSchools?.includes(schoolID))

// I colleghi che il maestro gestisce in questo circolo, per id
export const managedCoachIDsIn = (coach, schoolID) => {
	const ids = coach?.managedCoachesBySchool?.[schoolID]
	return Array.isArray(ids) ? ids : []
}

// Se le lezioni di questo collega, qui, le può modificare e assegnare
export const managesLessonsOf = (coach, schoolID, coachID) =>
	Boolean(
		coach &&
			coachID &&
			(managesSchool(coach, schoolID) || managedCoachIDsIn(coach, schoolID).includes(coachID)),
	)

// Le lezioni di chi si possono guardare, qui — nella giornata e nel
// riepilogo: le proprie, quelle che si modificano, e quelle che il
// proprietario ha fatto vedere (di tutti con `schoolPowers[schoolID].seesAll`,
// di alcuni con `seenCoachesBySchool`). Leggerle le regole lo permettono già
// a chiunque sia nel circolo: questo dice solo cosa mostrare.
export const seesAllLessonsIn = (coach, schoolID) =>
	managesSchool(coach, schoolID) || coach?.schoolPowers?.[schoolID]?.seesAll === true

export const seenCoachIDsIn = (coach, schoolID) => {
	const ids = coach?.seenCoachesBySchool?.[schoolID]
	return Array.isArray(ids) ? ids : []
}

export const seesLessonsOf = (coach, schoolID, coachID) =>
	Boolean(
		coach &&
			coachID &&
			(coachID === coach.coachID ||
				seesAllLessonsIn(coach, schoolID) ||
				seenCoachIDsIn(coach, schoolID).includes(coachID) ||
				managesLessonsOf(coach, schoolID, coachID)),
	)

// Insegna in questo circolo: ha una giornata sua, una colonna in agenda, e si
// sceglie per le lezioni. Nel circolo può starci anche chi non insegna — un
// direttore, la segreteria, il proprietario che non scende in campo
// (`notTeachingIn`, o un gestore di prima che si potesse scegliere): guarda
// le lezioni come dice il suo ruolo, ma tra i maestri non c'è.
//
// `teachesIn` del maestro collegato arriva in id; quello dei colleghi, letto
// così com'è, in reference.
export const teachesInSchool = (coach, schoolID) =>
	Boolean(schoolID) && (coach?.teachesIn ?? []).some(item => (item?.id ?? item) === schoolID)

// I maestri del circolo di cui si guardano le lezioni, sé compreso se
// insegna, nell'ordine del circolo: sono le voci del selettore del maestro.
// Chi non insegna non ha lezioni sue da guardare.
export const getViewableCoaches = (coach, school, schoolID) =>
	Object.values(school?.coaches ?? {}).filter(
		schoolCoach =>
			schoolCoach?.coachID &&
			teachesInSchool(schoolCoach, schoolID) &&
			seesLessonsOf(coach, schoolID, schoolCoach.coachID),
	)

// I maestri del circolo: sé per primo, poi i colleghi per nome. Solo chi
// insegna: a chi non scende in campo non si mettono lezioni. Servono i
// maestri già caricati: finché sono reference non si sa ancora come si
// chiamano, e intanto c'è solo sé.
export const getSchoolCoaches = (coach, school, schoolID) => {
	if (!coach) return []

	const colleagues = Object.values(school?.coaches ?? {})
		.filter(
			schoolCoach =>
				schoolCoach?.coachID &&
				schoolCoach.coachID !== coach.coachID &&
				teachesInSchool(schoolCoach, schoolID),
		)
		.sort((a, b) => (a.coachName ?? "").localeCompare(b.coachName ?? ""))

	return teachesInSchool(coach, schoolID) ? [coach, ...colleagues] : colleagues
}

// Per chi si può mettere una lezione in calendario, qui: per sé, e per i
// colleghi di cui si tengono le lezioni.
export const canAssignLessonTo = (coach, schoolID, coachID) =>
	Boolean(
		coach &&
			coachID &&
			(coachID === coach.coachID || managesLessonsOf(coach, schoolID, coachID)),
	)

export const getAssignableCoaches = (coach, school, schoolID) =>
	getSchoolCoaches(coach, school, schoolID).filter(schoolCoach =>
		canAssignLessonTo(coach, schoolID, schoolCoach.coachID),
	)

// Chi può toccare una lezione: chi tiene le lezioni del suo maestro, oppure
// il maestro stesso, purché insegni in questo circolo
export const canEditLesson = (coach, selectedSchool, lessonData) => {
	if (!coach || !lessonData) return false

	if (
		lessonData.coachID !== coach.coachID &&
		managesLessonsOf(coach, selectedSchool, lessonData.coachID)
	)
		return true

	const isLessonCoach = coach.coachID === lessonData.coachID

	// Una lezione tua la correggi tu, di qualunque tipo sia. Prima passavano
	// solo le private — `lessonType.includes("pri")` — che era un modo storto
	// di dire "quelle che fai tu", e ti chiudeva fuori da tutto il resto:
	// dalle tue SAT e CAD, da quelle di una tipologia che ti sei scritto, e
	// dalle tue di prima che le lezioni portassero una firma.
	//
	// Una lezione senza firma è di prima che si firmassero, e `isLessonCoach`
	// ha già detto che è tua: è tua. Quelle che un collega ti ha messo in
	// calendario la sua firma ce l'hanno, e restano sue — chi gestisce dei
	// maestri le tocca comunque, perché quello è il suo lavoro. Le presenze
	// e i sostituti, invece, li segni anche lì: quelli sono di chi c'è.
	const isMine = !lessonData.createdBy || lessonData.createdBy === coach.coachID
	const managesCoaches =
		managesSchool(coach, selectedSchool) || managedCoachIDsIn(coach, selectedSchool).length > 0

	return Boolean(
		isLessonCoach && teachesInSchool(coach, selectedSchool) && (managesCoaches || isMine),
	)
}

// ──────────────────────────────────────────────────────────────────────────
// UNO SPORT SOLO, PER ORA
//
// L'app nasce per il tennis, e chiedere lo sport a chi si registra era una
// domanda con una risposta sola. Chi insegna anche padel o atletica ce l'ha
// scritto sul suo documento e continua a vederli dove serve: il modello dei
// dati non cambia — `taughtSports` resta una lista — cambia solo quello che
// si chiede a chi arriva.
//
// Quando gli altri sport torneranno a valere, questa costante è il posto da
// cui ripartire: la riga in cima alla nuova lezione già li mostra a chi ne
// ha più di uno.
// ──────────────────────────────────────────────────────────────────────────
export const DEFAULT_SPORT = "tennis"

// ──────────────────────────────────────────────────────────────────────────
// COME SI COMPORTA UN TIPO DI LEZIONE, LO DICE IL TIPO DI LEZIONE
//
// Alcune lezioni non si svolgono sui campi del circolo: niente giocatori da
// nominare, niente campo, orario libero. Per un po' erano una lista qui —
// racchette in classe, centro estivo, live ball — e questo aveva due effetti
// buffi. Nell'interfaccia sembravano sport, perché stavano nella riga degli
// sport; e una tipologia che il maestro si fosse fatto da sé non poteva
// esserlo, perché in quella lista non c'era e nella lista non si scrive da
// fuori.
//
// Adesso lo dice il catalogo, e lo dice in due campi invece di uno, perché
// sono due domande diverse: `needsPlayers` è "si segnano i nomi di chi c'è",
// `needsCourt` è "si occupa un campo". Stavano insieme perché le tre lezioni
// di allora rispondevano no a tutt'e due, ma un live ball su un campo senza
// l'elenco dei presenti è una combinazione legittima, e prima non si poteva
// nemmeno dire.
//
// Le tabelle qui sotto restano come ripiego per i documenti che
// `scripts/seedLessonTypes.js` non ha ancora toccato: un catalogo vecchio e
// un'app nuova devono comportarsi come prima, non trasformare il centro
// estivo in una lezione con i nomi.
// ──────────────────────────────────────────────────────────────────────────
const LEGACY_WITHOUT_PLAYERS = ["racchette_in_classe", "centro_estivo", "live_ball"]
const LEGACY_WITHOUT_COURT = ["racchette_in_classe", "centro_estivo", "live_ball"]
const LEGACY_DURATION = { live_ball: 1.5 }

// Le lezioni di gruppo del circolo si mettono in calendario prima di sapere
// chi ci sarà: l'orario c'è, i nomi arrivano dopo — o non arrivano mai.
const LEGACY_NAMES_CAN_WAIT = ["sat", "cad", "prove_gratuite_sat", "prove_gratuite_cad"]

// Il SAT si fa in più maestri sullo stesso campo: lì gli assistenti si
// propongono da soli. Il CAD no, ma un circolo può accenderlo.
const LEGACY_ALLOWS_ASSISTANTS = ["sat"]

// Come si legge un tipo di lezione quando il catalogo non ha ancora detto
// tutto. Lo spread sta in fondo: quello che il documento dice vince.
export const withLessonTypeDefaults = (lessonTypeID, lessonType) => ({
	needsPlayers: !LEGACY_WITHOUT_PLAYERS.includes(lessonTypeID),
	needsCourt: !LEGACY_WITHOUT_COURT.includes(lessonTypeID),
	defaultDuration: LEGACY_DURATION[lessonTypeID] ?? 1,
	color: legacyLessonTypeColor(lessonTypeID),
	namesCanWait: LEGACY_NAMES_CAN_WAIT.includes(lessonTypeID),
	allowsAssistants: LEGACY_ALLOWS_ASSISTANTS.includes(lessonTypeID),
	...lessonType,
})

// Se di questa lezione si segnano i nomi di chi c'è. Un tipo che non
// conosciamo li ha: il giro normale è il ripiego giusto.
export const lessonTypeHasPlayers = lessonType => lessonType?.needsPlayers !== false

// ──────────────────────────────────────────────────────────────────────────
// I NOMI SI POSSONO SEGNARE DOPO
//
// Un SAT o un CAD si fissa per l'ora, non per chi c'è: i posti si mettono
// subito, vuoti, e i nomi si scrivono quando si sanno. Un posto vuoto è un
// segnaposto — niente nome e niente allievo dietro — e non diventa mai un
// allievo del circolo, né un pagamento: resta un numero nella lezione.
// ──────────────────────────────────────────────────────────────────────────
export const lessonTypeNamesCanWait = lessonType =>
	lessonTypeHasPlayers(lessonType) && lessonType?.namesCanWait === true

export const isPlaceholderPartecipant = partecipant =>
	!partecipant?.playerID && !partecipant?.playerName?.trim()

// Il segnaposto non ha un nome: si chiama per il posto che occupa.
export const getPlaceholderLabel = index => `Allievo ${index + 1}`

// I nomi di chi c'è davvero, senza i posti ancora da riempire.
export const getNamedPartecipants = partecipants =>
	(partecipants ?? []).filter(partecipant => !isPlaceholderPartecipant(partecipant))

// Quante persone diverse ci sono in un gruppo di lezioni: chi viene a dieci
// lezioni è una persona, non dieci. Si riconosce dall'allievo, o dal nome
// per chi è stato scritto senza scheda; un posto vuoto non si sa chi sia, e
// non si conta. Gli assenti sì: sono allievi di quel corso anche quel giorno.
export const countDistinctPartecipants = lessons => {
	const people = new Set()
	;(lessons ?? []).forEach(lesson =>
		getNamedPartecipants(lesson.partecipants).forEach(partecipant =>
			people.add(partecipant.playerID || partecipant.playerName.trim().toLowerCase()),
		),
	)
	return people.size
}

// ──────────────────────────────────────────────────────────────────────────
// LE PRIVATE SONO IL LORO NUMERO
//
// Un PRI 2 è la privata da due: spostargli il minimo a tre vorrebbe dire un
// PRI 2 da tre, che non vuol dire niente, e soprattutto vorrebbe dire che
// tutte le lezioni salvate come PRI 2 adesso raccontano un'altra cosa.
//
// Non è una lista di quali siano — sarebbe da riscrivere ogni volta che il
// catalogo cambia. Una tipologia il cui minimo e massimo coincidono *è*
// quel numero, e quel numero non si tocca; una che sta in un intervallo —
// il CAD da 2 a 6, il SAT da 3 in su — l'intervallo lo decide il circolo.
// ──────────────────────────────────────────────────────────────────────────
export const isExactPlayerCount = lessonType =>
	Boolean(lessonType?.minimumPlayers) && lessonType.minimumPlayers === lessonType.maxPlayers

// ──────────────────────────────────────────────────────────────────────────
// PIÙ ISTRUTTORI IN CAMPO
//
// Un corso con tanti allievi si fa anche in due o tre maestri: il titolare
// mette gli altri come assistenti. In una privata no — ha il suo numero di
// allievi e un maestro solo — e per lo stesso motivo per cui il suo numero
// non si tocca (`isExactPlayerCount`), l'interruttore non le si propone
// nemmeno, e quello che fosse rimasto scritto non conta.
// ──────────────────────────────────────────────────────────────────────────
export const lessonTypeAllowsAssistants = lessonType =>
	lessonType?.allowsAssistants === true && !isExactPlayerCount(lessonType)

// Se questa lezione occupa un campo del circolo — e quindi se il campo si
// sceglie, si paga e finisce nel conto delle ore.
export const lessonTypeHasCourt = lessonType => lessonType?.needsCourt !== false

// Durata proposta quando si sceglie l'ora di inizio — il live ball va a slot
// di un'ora e mezza. Resta comunque modificabile.
export const getDefaultLessonDuration = lessonType => lessonType?.defaultDuration ?? 1

// L'orario che il modulo propone, da un inizio e una durata. Una lezione
// però non passa la mezzanotte: aperta alle 23 la proposta era
// 23:00-00:00, con la fine prima dell'inizio, e "Avanti" restava spento
// senza dire perché. Quando la fine cadrebbe nel giorno dopo, la lezione
// proposta è l'ultima che finisce alle 23. Vale per l'orario di partenza e
// per quello che si ricalcola cambiando tipologia: una da due ore scelta
// alle 22 finirebbe a mezzanotte anche lei.
export const lessonTimesWithinDay = (start, duration = 1) => {
	const crossesMidnight = !start.plus({ hours: duration }).hasSame(start, "day")
	const fitted = crossesMidnight
		? start.set({ hour: 23, minute: 0, second: 0, millisecond: 0 }).minus({ hours: duration })
		: start

	return {
		start: fitted.toFormat("HH:mm"),
		end: fitted.plus({ hours: duration }).toFormat("HH:mm"),
	}
}

// L'orario proposto a una lezione nuova: dall'ora di adesso, per la durata
// del tipo.
export const defaultLessonTimes = (now, duration = 1) =>
	lessonTimesWithinDay(now.set({ minute: 0, second: 0, millisecond: 0 }), duration)

// ──────────────────────────────────────────────────────────────────────────
// IL LIVELLO DEL SAT
//
// I bambini del SAT giocano con la palla del loro livello: red, orange,
// green, yellow. Dirlo è facoltativo — chi non lo segna salva la lezione
// come prima — e vale solo per il SAT: un CAD o una privata un livello
// così non ce l'hanno.
// ──────────────────────────────────────────────────────────────────────────
export const SAT_LEVELS = [
	{ value: "red", label: "Red", color: "level_red" },
	{ value: "orange", label: "Orange", color: "level_orange" },
	{ value: "green", label: "Green", color: "level_green" },
	{ value: "yellow", label: "Yellow", color: "level_yellow" },
]

export const lessonTypeHasSatLevel = lessonTypeID => lessonTypeID === "sat"

export const getSatLevel = value => SAT_LEVELS.find(level => level.value === value) ?? null

// Il livello come va scritto sulla lezione: solo uno di quei quattro, e
// solo in un SAT. Tutto il resto è `null`, così cambiare tipologia non si
// porta dietro un livello che lì non vuol dire niente.
export const satLevelToSave = (lessonTypeID, satLevel) =>
	lessonTypeHasSatLevel(lessonTypeID) && getSatLevel(satLevel) ? satLevel : null

// ──────────────────────────────────────────────────────────────────────────
// IL LIVELLO PTR DEL CAD
//
// Gli adulti del CAD si misurano sulla scala PTR, da 1.0 a 5.0 a mezzi
// punti: oltre, nei corsi adulti, non ci si arriva. Come per il SAT dirlo è
// facoltativo, e vale solo per il CAD.
//
// La scala e le sue descrizioni stanno solo qui: le leggono il modulo della
// lezione, la sua guida e l'agenda, la scheda tecnica dell'allievo, e
// domani il quaderno. Sulla lezione si scrive il numero (3, 3.5),
// l'etichetta è quella che si legge ("3.0").
// ──────────────────────────────────────────────────────────────────────────
export const PTR_LEVELS = [
	{
		value: 1,
		label: "1.0",
		description: "Principiante assoluto, al primo approccio con il tennis.",
	},
	{
		value: 1.5,
		label: "1.5",
		description:
			"Poca esperienza; sa rimandare la palla ma ha grossa difficoltà nei colpi fondamentali.",
	},
	{
		value: 2,
		label: "2.0",
		description:
			"Inizia a controllare la direzione dei colpi ma manca di continuità e mobilità.",
	},
	{
		value: 2.5,
		label: "2.5",
		description:
			"Migliora la visione di campo, palleggia a ritmo lento ma sbaglia spesso il servizio.",
	},
	{
		value: 3,
		label: "3.0",
		description:
			"Scambia a velocità media, controlla la direzione ma va in difficoltà fuori dalla sua zona di comfort.",
	},
	{
		value: 3.5,
		label: "3.5",
		description:
			"Discreto controllo e direzionalità; sa variare il gioco ma è incostante nei colpi complessi.",
	},
	{
		value: 4,
		label: "4.0",
		description:
			"Giocatore solido: controllo con profondità, buona copertura di campo e tattica di base (4ª categoria).",
	},
	{
		value: 4.5,
		label: "4.5",
		description:
			"Ottima potenza e controllo; riduce al minimo gli errori non forzati e gestisce bene il doppio.",
	},
	{
		value: 5,
		label: "5.0",
		description:
			"Avanzato/agonista (3ª categoria): anticipa il gioco, colpi puliti, adatta la strategia all'avversario.",
	},
]

export const lessonTypeHasPtrLevel = lessonTypeID => lessonTypeID === "cad"

export const getPtrLevel = value => PTR_LEVELS.find(level => level.value === value) ?? null

// Come per il SAT: solo un livello della scala, e solo in un CAD.
export const ptrLevelToSave = (lessonTypeID, ptrLevel) =>
	lessonTypeHasPtrLevel(lessonTypeID) && getPtrLevel(ptrLevel) ? ptrLevel : null

// Il selettore del maestro ha senso solo se in questo circolo c'è davvero
// qualcun altro da guardare: un collega che insegna, di cui si vedono o si
// modificano le lezioni.
export const canSwitchDisplayedCoach = (coach, school, schoolID) => {
	if (!coach || !school) return false

	// finché i coach del circolo non sono stati caricati sono reference, non
	// maestri: non contano
	const otherCoaches = Object.values(school.coaches ?? {}).filter(
		schoolCoach =>
			schoolCoach?.coachID &&
			schoolCoach.coachID !== coach.coachID &&
			teachesInSchool(schoolCoach, schoolID),
	)
	if (otherCoaches.length === 0) return false

	return otherCoaches.some(schoolCoach => seesLessonsOf(coach, schoolID, schoolCoach.coachID))
}

// Il maestro da mostrare aprendo le lezioni: sé, se si insegna qui. Chi non
// insegna non ha una giornata sua, sempre vuota: parte dal primo dei
// maestri che può guardare. Se non ne guarda nessuno resta su di sé, e
// trova le lezioni vuote che sono davvero le sue.
export const defaultDisplayedCoachID = (coach, school, schoolID) => {
	if (!coach) return null
	if (teachesInSchool(coach, schoolID)) return coach.coachID

	const [first] = getViewableCoaches(coach, school, schoolID).sort((a, b) =>
		(a.coachName ?? "").localeCompare(b.coachName ?? ""),
	)
	return first?.coachID ?? coach.coachID
}

// La barra arancione in cima serve solo a passare da un circolo all'altro:
// con un circolo solo non c'è niente da scegliere, e resta una fascia che
// occupa spazio senza fare niente. Contano i circoli del selettore (vedi
// `switchableSchoolIDs`): uno in uso e uno archiviato sono un circolo solo.
export const canSwitchSchool = schoolIDs => (schoolIDs?.length ?? 0) > 1

// ──────────────────────────────────────────────────────────────────────────
// LA PRIVATA È UNA SOLA, DA QUANTI SI VUOLE
//
// Nel catalogo le private sono una per numero di allievi — pri1, pri2, pri3,
// pri4 — perché ognuna ha il suo prezzo. Per chi mette una lezione in
// calendario però sono una cosa sola: una privata, e poi quanti sono. Il
// modulo propone "Privata" accanto al SAT e al CAD, e il numero di giocatori
// sceglie quale delle quattro.
//
// Per lo stesso motivo "PRI 2" non si legge più: si legge "Privata · 2
// giocatori". Il nome sul catalogo resta quello — è quello che va nel titolo
// dell'evento su Google Calendar, e se il telefono e il server lo
// scrivessero diverso l'evento verrebbe riscritto a ogni controllo.
// ──────────────────────────────────────────────────────────────────────────
export const PRIVATE_LESSON_LABEL = "Privata"

const PRIVATE_LESSON_TYPE = /^pri(\d+)$/

// Quanti allievi ha una privata, o `null` se non è una privata.
export const privateLessonSize = lessonTypeID => {
	const match = PRIVATE_LESSON_TYPE.exec(lessonTypeID ?? "")
	return match ? Number(match[1]) : null
}

export const isPrivateLessonType = lessonTypeID => privateLessonSize(lessonTypeID) !== null

export const playersCountLabel = count => (count === 1 ? "1 giocatore" : `${count} giocatori`)

// ──────────────────────────────────────────────────────────────────────────
// I CORSI SI CHIAMANO PER NOME
//
// Come le private, anche i corsi non si leggono più con la sigla: SAT è la
// scuola tennis, CAD il corso adulti, e chi apre l'app per la prima volta
// non deve impararlo. Il nome sul catalogo resta la sigla, per lo stesso
// motivo delle private: è quello che va nel titolo dell'evento su Google
// Calendar, e scritto diverso farebbe riscrivere tutti gli eventi.
//
// Un circolo abituato alle sigle può tenerle: nelle impostazioni del corso
// accende "Usa una sigla" (`useShortName`, `shortName` nella sua correzione)
// e la sceglie. Vale solo lì. Le prove gratuite seguono il loro corso.
// ──────────────────────────────────────────────────────────────────────────
export const COURSE_NAMES = { sat: "Scuola tennis", cad: "Corso adulti" }

// come si dice "di quel corso" in una frase: la scuola è femminile
const COURSE_IN_SENTENCE = { sat: "della scuola tennis", cad: "del corso adulti" }

const TRIAL_COURSE = { prove_gratuite_sat: "sat", prove_gratuite_cad: "cad" }

// Se a questo tipo di lezione si può dare una sigla: ai corsi.
export const lessonTypeTakesShortName = lessonTypeID => Boolean(COURSE_NAMES[lessonTypeID])

// La sigla che il circolo usa per questo corso, o `null` se usa il nome.
export const courseShortName = lessonType =>
	lessonType?.useShortName === true && lessonType.shortName?.trim()
		? lessonType.shortName.trim()
		: null

// Come si legge un tipo di lezione, dati i tipi come li vede il circolo.
export const lessonTypeDisplayName = (lessonTypeID, lessonTypes) => {
	const lessonType = lessonTypes?.[lessonTypeID]
	const trialOf = TRIAL_COURSE[lessonTypeID]
	if (trialOf)
		return `Prova ${courseShortName(lessonTypes?.[trialOf]) ?? COURSE_NAMES[trialOf].toLowerCase()}`
	return (
		courseShortName(lessonType) ??
		COURSE_NAMES[lessonTypeID] ??
		lessonType?.lessonTypeName ??
		lessonTypeID
	)
}

// Ogni tipo con il suo `displayName`: è quello che si legge, mentre
// `lessonTypeName` resta il nome del catalogo, per Calendar.
export const withDisplayNames = lessonTypes =>
	Object.fromEntries(
		Object.entries(lessonTypes ?? {}).map(([lessonTypeID, lessonType]) => [
			lessonTypeID,
			{ ...lessonType, displayName: lessonTypeDisplayName(lessonTypeID, lessonTypes) },
		]),
	)

// Il nome che si legge di un tipo di lezione già risolto.
export const lessonTypeDisplayNameOf = (lessonType, lessonTypeID) =>
	lessonType?.displayName ?? lessonType?.lessonTypeName ?? lessonTypeID ?? ""

// "Di quel corso" dentro una frase: "Hanno un recupero del corso adulti",
// "…del CAD" se il circolo usa la sigla.
export const courseInSentence = (lessonTypeID, lessonType) => {
	const shortName = courseShortName(lessonType)
	if (shortName) return `del ${shortName}`
	return COURSE_IN_SENTENCE[lessonTypeID] ?? `di ${lessonTypeDisplayNameOf(lessonType, lessonTypeID)}`
}

// Come si legge un tipo di lezione in un riepilogo o in un listino.
export const getLessonTypeLabel = (lessonTypeID, lessonType) => {
	const size = privateLessonSize(lessonTypeID)
	if (size !== null) return `${PRIVATE_LESSON_LABEL} · ${playersCountLabel(size)}`
	return lessonTypeDisplayNameOf(lessonType, lessonTypeID)
}

// Le private che si fanno qui, dalla più piccola alla più grande.
export const sortPrivateLessonTypes = lessonTypeIDs =>
	lessonTypeIDs
		.filter(isPrivateLessonType)
		.sort((a, b) => privateLessonSize(a) - privateLessonSize(b))

// La privata giusta per questo numero di allievi: quella che ce l'ha, o la
// più piccola che li contiene. Oltre la più grande si resta sulla più
// grande, che è anche il massimo che il modulo lascia scegliere.
export const privateLessonTypeFor = (playersCount, privateLessonTypes) =>
	privateLessonTypes.find(lessonTypeID => privateLessonSize(lessonTypeID) >= playersCount) ??
	privateLessonTypes[privateLessonTypes.length - 1] ??
	null

export const getPaymentAmount = (prices, lessonType, sportID, lessonDuration) => {
	const basePrice = prices?.[sportID]?.[lessonType] ?? 0
	return Math.ceil(basePrice * (lessonDuration || 0))
}

export const getPaymentAmountByType = playersInLesson => {
	var paymentAmountByType = {
		cash: 0,
		satispay: 0,
		bankTransfer: 0,
		pos: 0,
		subscription: 0,
		recovery: 0,
	}
	Object.keys(playersInLesson).forEach(player => {
		playersInLesson[player].paymentMethod === "subscription" ||
		playersInLesson[player].paymentMethod === "recovery"
			? paymentAmountByType[[playersInLesson[player].paymentMethod]]++
			: (paymentAmountByType[[playersInLesson[player].paymentMethod]] +=
					playersInLesson[player].paymentAmount)
	})
	return paymentAmountByType
}

// L'importo già salvato su una lezione, o il ripiego quando non c'è niente
// da cui ripartire — un allievo aggiunto a una lezione che esiste già non ha
// un importo suo. `parseInt` su quel niente torna `NaN`, e `??` intercetta
// `null` e `undefined` ma non `NaN`: prima quell'allievo nasceva con importo
// `NaN` invece del prezzo di listino, e la sua quota spariva dagli incassi.
//
// `parseInt` andava bene finché i prezzi erano interi: da quando il campo
// pagato dagli allievi si scala dal listino un importo può avere i centesimi,
// e troncarli li faceva sparire a ogni modifica della lezione.
export const savedPaymentAmount = (saved, fallback) => {
	// `Number(null)` e `Number("")` fanno zero, ma un importo che non c'è non
	// è un allievo che paga zero: è un allievo senza importo
	if (saved === null || saved === undefined || saved === "") return fallback

	const amount = Number(saved)
	return Number.isNaN(amount) ? fallback : amount
}

// Abbonamento e recupero non hanno un importo proprio: la lezione è già
// pagata altrove, o è il recupero di una pagata prima. Valgono zero, e non
// c'è listino che li riguardi.
export const paymentCoveredElsewhere = paymentMethod =>
	paymentMethod === "subscription" || paymentMethod === "recovery"

// Il nome di un metodo di pagamento. Abbonamento e recupero l'app li propone
// da sé, anche dove il catalogo non li ha: il nome non può mancare.
const PAYMENT_METHOD_NAMES = { subscription: "Abbonamento", recovery: "Recupero" }

export const getPaymentMethodName = (getPaymentMethod, paymentMethod) =>
	getPaymentMethod(paymentMethod)?.paymentName ?? PAYMENT_METHOD_NAMES[paymentMethod] ?? ""

// Quanto paga questo allievo dopo che la lezione è cambiata. Di norma decide
// il listino — cambiare durata o numero di allievi cambia il prezzo — ma un
// importo che il maestro ha corretto a mano resta suo: altrimenti sparirebbe
// al primo ritocco della lezione, e senza che nessuno glielo dica.
export const paymentAmountAfterEdit = (player, listPrice) => {
	if (!player) return listPrice
	if (paymentCoveredElsewhere(player.paymentMethod)) return 0
	if (!player.paymentAmountIsCustom) return listPrice
	return savedPaymentAmount(player.paymentAmount, listPrice)
}

// Marca gli allievi il cui importo è una correzione del maestro. Da quando
// esiste il marchio lo si mette nel momento in cui si corregge; per le
// lezioni salvate prima lo si deduce da com'erano: un importo diverso dal
// prezzo che il listino dava a quella lezione non può che essere voluto.
export const markCustomPaymentAmounts = (partecipants, savedListPrice) =>
	partecipants.map(player => ({
		...player,
		paymentAmountIsCustom:
			player.paymentAmountIsCustom ??
			(!paymentCoveredElsewhere(player.paymentMethod) &&
				savedPaymentAmount(player.paymentAmount, savedListPrice) !== savedListPrice),
	}))

// Chi paga di tasca sua lascia un movimento in `payments`; l'abbonamento no,
// perché quella lezione è già stata pagata altrove. Il recupero sì, anche se
// vale zero: è comunque una lezione che qualcuno ha usato.
export const hasLessonPayment = player =>
	Boolean(player?.playerID) &&
	Boolean(player?.paymentMethod) &&
	player.paymentMethod !== "subscription"

// Un allievo ha un pagamento per lezione, e uno solo. Dati i pagamenti già
// salvati per una lezione e i partecipanti che ha adesso, dice a quale
// documento tocca il pagamento di ciascuno — quello di prima se c'era, uno
// nuovo altrimenti — e quali documenti non hanno più ragione di esistere:
// l'allievo è uscito dalla lezione, è passato all'abbonamento, oppure è un
// doppione di quando ogni modifica ne aggiungeva uno invece di riusarlo.
//
// `savedPayments` sono `{ playerID, ref }`, `newPaymentRef()` fabbrica il
// riferimento di un pagamento nuovo: qui dentro non si scrive niente, si
// decide soltanto.
export const reconcileLessonPayments = ({
	savedPayments = [],
	partecipants = [],
	newPaymentRef,
}) => {
	const savedByPlayer = new Map()
	const obsoletePayments = []

	savedPayments.forEach(({ playerID, ref }) => {
		if (playerID && !savedByPlayer.has(playerID)) savedByPlayer.set(playerID, ref)
		else obsoletePayments.push(ref)
	})

	const paymentByPlayer = new Map()
	partecipants.filter(hasLessonPayment).forEach(player => {
		if (paymentByPlayer.has(player.playerID)) return
		const savedRef = savedByPlayer.get(player.playerID)
		paymentByPlayer.set(player.playerID, {
			ref: savedRef ?? newPaymentRef(),
			isNew: !savedRef,
		})
	})

	savedByPlayer.forEach((ref, playerID) => {
		if (!paymentByPlayer.has(playerID)) obsoletePayments.push(ref)
	})

	return { paymentByPlayer, obsoletePayments }
}

export const asyncForEach = async (array, callback) => {
	for (let index = 0; index < array.length; index++) {
		await callback(array[index], index, array)
	}
}

export const normalizeSchoolIDs = refs =>
	Array.isArray(refs) ? refs.filter(r => r && r.path).map(r => r.path.split("/")[1]) : []

export const getDayOfWeek = selectedDate => {
	var isToday = DateTime.fromISO(selectedDate).toFormat("D") === DateTime.now().toFormat("D")
	var isTomorrow =
		DateTime.fromISO(selectedDate).toFormat("D") ===
		DateTime.now().plus({ days: 1 }).toFormat("D")
	var isYesterday =
		DateTime.fromISO(selectedDate).toFormat("D") ===
		DateTime.now().minus({ days: 1 }).toFormat("D")
	if (isToday) return `oggi`
	else if (isTomorrow) return `domani`
	else if (isYesterday) return `ieri`
	else return DateTime.fromISO(selectedDate).setLocale("it").toFormat("ccc d LLL")
}

// ──────────────────────────────────────────────────────────────────────────
// LA TAVOLOZZA DEI TIPI DI LEZIONE
//
// Ogni tinta è una coppia: la chiara fa da sfondo alla lezione nell'elenco, la
// scura colora i bottoni, il tassello del riepilogo e il testo sopra la
// chiara. Sono le tinte che il tema tiene per le lezioni, e una tipologia
// nuova pesca da qui: inventarne una a mano vorrebbe dire una schermata che
// non si riconosce più.
//
// `tileBg` e `tileText` esistono solo dove il tassello del riepilogo non usa
// la stessa coppia dello sfondo, e non perché sia giusto: è come è sempre
// stato, e cambiarlo adesso ricolorerebbe il passato di chi guarda i suoi
// riepiloghi. Le private sono il caso interessante — la loro lezione in
// elenco è bianca, e il tassello no.
// ──────────────────────────────────────────────────────────────────────────
export const LESSON_TYPE_COLORS = {
	// La tinta delle private. La chiave si chiama `gunmetal` da prima che il
	// tema avesse un nome per loro — sta dentro le tipologie già salvate, e
	// rinominarla vorrebbe dire cambiargli colore sotto i piedi — ma i colori
	// che indica sono quelli che il tema tiene per le private, che nello
	// scuro non sono gli stessi del chiaro.
	gunmetal: {
		light: "surface",
		dark: "gunmetal",
		fill: "pri_fill",
		tileBg: "pri",
		tileText: "pri_dark",
	},
	sat: { light: "sat", dark: "sat_dark", fill: "sat_fill" },
	cad: { light: "cad", dark: "cad_dark", fill: "cad_fill" },
	ric: { light: "ric", dark: "ric_dark", fill: "ric_fill" },
	ce: { light: "ce", dark: "ce_dark", fill: "ce_fill" },
	lb: { light: "lb", dark: "lb_dark", fill: "lb_fill" },
	blue_t: {
		light: "blue_t",
		dark: "blue_t_dark",
		fill: "blue_t_fill",
		tileBg: "blue_light",
		tileText: "blue",
	},
	// Le partite (src/utils/match.utils.js): non sono una tipologia, e il
	// loro grigio non si sceglie per una tipologia.
	match: { light: "match", dark: "match_dark", fill: "match_fill" },
	// Il colore di chi non ne ha uno: la lezione in elenco prende il verde
	// delle racchette e il tassello resta grigio. Non è una scelta, è quello
	// che facevano le due catene di `if` quando non riconoscevano un tipo.
	none: {
		light: "ric",
		dark: "ric_dark",
		fill: "ric_fill",
		tileBg: "gray_light",
		tileText: "gunmetal",
	},
}

// Quali tinte si possono scegliere per una tipologia propria: tutte quelle
// vere. `none` non è una tinta, è l'assenza di una scelta, e `match` è delle
// partite: una tipologia di quel grigio sembrerebbe una partita.
export const PICKABLE_LESSON_TYPE_COLORS = Object.keys(LESSON_TYPE_COLORS).filter(
	colorKey => colorKey !== "none" && colorKey !== "match",
)

// Il colore che un tipo di lezione aveva prima che il colore fosse un dato.
// Era una catena di `includes` letta dall'alto, e l'ordine conta: `sat_pro`
// contiene "sat", e `blue` veniva prima di tutti.
const LEGACY_COLORS = [
	["blue", "blue_t"],
	["sat", "sat"],
	["cad", "cad"],
	["pri", "gunmetal"],
	["ric", "ric"],
]

const LEGACY_COLOR_BY_ID = { centro_estivo: "ce", live_ball: "lb", racchette_in_classe: "ric" }

export const legacyLessonTypeColor = lessonTypeID => {
	const byID = LEGACY_COLOR_BY_ID[lessonTypeID]
	if (byID) return byID

	const matched = LEGACY_COLORS.find(([fragment]) => lessonTypeID?.includes(fragment))
	return matched ? matched[1] : "none"
}

const paletteOf = lessonType => LESSON_TYPE_COLORS[lessonType?.color] ?? LESSON_TYPE_COLORS.none

// Lo sfondo della lezione in elenco, o la tinta cupa per quello che ci va
// sopra: scritte, icone, bordi. Nello scuro la tinta cupa diventa chiara —
// deve staccare dal fondo — e quindi sotto a una scritta bianca non ci va.
export const getLessonColor = (lessonType, getDarkerColors) => {
	const palette = paletteOf(lessonType)
	return theme.colors[getDarkerColors ? palette.dark : palette.light]
}

// Il pieno del tipo di lezione: il fondo sotto a una scritta bianca —
// bottoni, pastiglie scelte, interruttori — che resta scuro in tutti e due i
// temi. Nel chiaro coincide con la tinta cupa, nello scuro no.
export const getLessonFillColor = lessonType => theme.colors[paletteOf(lessonType).fill]

// Il colore con cui un tipo di lezione si riconosce nei tasselli del
// riepilogo e nell'elenco che li modifica: stessa tinta nei due posti, così
// quello che si tocca e quello che si apre sono evidentemente la stessa cosa.
export const getLessonTypeTileColors = lessonType => {
	const palette = paletteOf(lessonType)

	return {
		bg: theme.colors[palette.tileBg ?? palette.light],
		text: theme.colors[palette.tileText ?? palette.dark],
	}
}

export const getSportIcon = sportID => {
	if (sportID === "tennis") return faBaseball
	else if (sportID === "padel") return faTableTennisPaddleBall
	else if (sportID === "atletica") return faPersonRunning
}

// Luogo dell'evento su Google Calendar. Google riconosce il posto molto
// più spesso se riceve "Nome del circolo, via e città" invece del solo
// indirizzo: il nome aggancia il punto di interesse, l'indirizzo lo
// disambigua. Se ne manca uno si usa l'altro; se mancano entrambi
// l'evento resta senza luogo.
//
// Il nome che Maps riconosce però è spesso più lungo di quello che si
// vuole leggere nell'app — "Siro Tennis S.S.D." contro "Siro" — e quello
// corto può essere ambiguo: cercando "Siro" Google propone anche la
// trattoria omonima. Per questo il campo facoltativo `mapsName`, se c'è,
// vince sul nome mostrato nell'interfaccia.
export const buildEventLocation = school => {
	const name = (school?.mapsName || school?.schoolName)?.trim() ?? ""
	const address = school?.address?.trim() ?? ""

	if (!address) return name
	if (!name) return address
	// l'indirizzo a volte comincia già col nome del circolo
	if (address.toLowerCase().includes(name.toLowerCase())) return address

	return `${name}, ${address}`
}
